import { useEffect, useState, Fragment } from "react";
import { useIntl } from "react-intl";

import { formatDistanceToNow } from "date-fns";

// MUI
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    IconButton,
    Menu,
    Modal,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    Tabs,
    TextField,
    Typography,
    InputAdornment,
    MenuItem,
    Tooltip,
    TableContainer,
    Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";

// Components
import { Checkbox } from "components/Checkbox";
import { LinkButton } from "components/Buttons";
import { CircledElement } from "../../../ProjectDashboard/styled-components";
import CustomIcon from "components/CustomIcon";
import { CustomTabContent } from "components/CustomTabContent";

// Hooks
import useInviteUsersModal from "./useInviteUsersModal";

// Types
import { TInviteUsersModal } from "./InviteUsersModal.types";

// Styles
import useStyles from "./InviteModal.styles";

import icons from "enums/icons";

const InviteUsersModal = ({
    open,
    activeTab,
    onClose,
    pendingInvitations,
    org_id,
    fetchUsersData,
}: TInviteUsersModal) => {
    const classes = useStyles();
    const intl = useIntl();

    const [tabIndex, setTabIndex] = useState<number>(activeTab);
    const [selectedInvitations, setSelectedInvitations] = useState<number[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [isValidated, setIsValidated] = useState(false);

    const {
        openMessages,
        anchorEl,
        openSubmenu,
        invitationsData,
        filteredInv,
        areFieldsValid,
        setAnchorEl,
        setOpenMessages,
        handleOpenMessage,
        handleOnChange,
        handleAddInvitations,
        removeInvitation,
        handleSend,
        handleFilterChange,
        handleCheckboxChange,
        handleSendReminder,
        handleCloseModal,
        getNameFieldError,
        getEmailFieldError,
    } = useInviteUsersModal(pendingInvitations, org_id, selectedInvitations, setSelectedInvitations, onClose);

    useEffect(() => {
        setTabIndex(activeTab);
        setOpenMessages([false]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const handleTabChange = (ev: React.SyntheticEvent, newTabIndex: number) => {
        setTabIndex(newTabIndex);
    };

    const sendInvitations = async () => {
        setIsSending(true);

        const hasInviteError = invitationsData.some((inv) => inv.errorData?.status === "INVITE_ALREADY_EXISTS");

        if (hasInviteError) {
            await handleSendReminder();
        } else {
            await handleSend();
        }

        setIsValidated(true);
        setIsSending(false);
        fetchUsersData();
    };

    useEffect(() => {
        if (!isSending && isValidated) {
            const hasErrors = invitationsData.some(
                (invitation) =>
                    invitation.errorData?.readable_error_segments &&
                    invitation.errorData?.readable_error_segments.length > 0
            );

            if (!hasErrors) {
                onClose();
                setIsValidated(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invitationsData, isSending, isValidated]);

    return (
        <Modal
            open={open}
            onClose={handleCloseModal}
            slotProps={{
                backdrop: {
                    className: classes.backdrop,
                },
            }}
        >
            <Box className={classes.modalWrapper}>
                <Box display="flex" justifyContent="space-between">
                    {/* Tabs */}
                    <Box display="flex">
                        <Tabs
                            value={tabIndex}
                            onChange={handleTabChange}
                            className={classes.tabStyle}
                            TabIndicatorProps={{
                                sx: {
                                    bottom: "8px",
                                    height: "2px",
                                },
                            }}
                        >
                            <Tab
                                disableRipple
                                className={classes.tabStyle}
                                label={
                                    <Typography variant="subtitle2" sx={{ textTransform: "none" }}>
                                        {intl.formatMessage({ id: "hubManagement.client.addUsers.inviteUser" })}
                                    </Typography>
                                }
                            />
                            <Tab
                                disableRipple
                                className={classes.tabStyle}
                                label={
                                    <Box display="flex">
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ textTransform: "none", marginRight: "4px" }}
                                        >
                                            {intl.formatMessage({ id: "hubManagement.client.addUsers.pendingUsers" })}
                                        </Typography>
                                        {pendingInvitations.length ? (
                                            <CircledElement color="warning" size={20}>
                                                {pendingInvitations.length}
                                            </CircledElement>
                                        ) : null}
                                    </Box>
                                }
                                disabled={!pendingInvitations}
                            />
                        </Tabs>
                    </Box>

                    <IconButton size="small" onClick={handleCloseModal}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Tab Card Content for Invite Users*/}
                <CustomTabContent value={tabIndex} index={0}>
                    <Box className={classes.description}>
                        <Typography variant="subtitle2">
                            {intl.formatMessage({ id: "hubManagement.client.addUsers.inviteUser.description" })}
                        </Typography>
                    </Box>
                    <Box className={classes.formContainer}>
                        {invitationsData.map((invitation) => (
                            <Fragment key={invitation.id}>
                                <Box display="flex" justifyContent="space-between">
                                    <FormControl fullWidth sx={{ padding: "8px" }}>
                                        <FormLabel sx={{ fontSize: "10px", fontWeight: 500, marginBottom: "8px" }}>
                                            Name
                                        </FormLabel>
                                        <TextField
                                            id={`name-${invitation.id}`}
                                            variant="standard"
                                            fullWidth
                                            className={classes.textField}
                                            placeholder={intl.formatMessage({
                                                id: "hubManagement.client.addUsers.inviteUser.namePlaceholder",
                                            })}
                                            value={invitation.name}
                                            onChange={(e) => handleOnChange(invitation.id, "name", e.target.value)}
                                            error={Boolean(getNameFieldError(invitation))}
                                            helperText={getNameFieldError(invitation)}
                                        />
                                    </FormControl>
                                    <FormControl fullWidth sx={{ padding: "8px" }}>
                                        <FormLabel sx={{ fontSize: "10px", fontWeight: 500, marginBottom: "8px" }}>
                                            Email
                                        </FormLabel>
                                        <TextField
                                            id={`email-${invitation.id}`}
                                            variant="standard"
                                            fullWidth
                                            className={classes.textField}
                                            placeholder={intl.formatMessage({
                                                id: "hubManagement.client.addUsers.inviteUser.emailPlaceholder",
                                            })}
                                            value={invitation.email}
                                            onChange={(e) => handleOnChange(invitation.id, "email", e.target.value)}
                                            error={Boolean(getEmailFieldError(pendingInvitations, invitation))}
                                            helperText={getEmailFieldError(pendingInvitations, invitation)}
                                        />
                                    </FormControl>
                                    {invitation.id > 0 && (
                                        <IconButton
                                            size="small"
                                            aria-label="remove"
                                            className={classes.removeButton}
                                            onClick={() => removeInvitation(invitation.id)}
                                        >
                                            <DeleteOutlinedIcon />
                                        </IconButton>
                                    )}
                                </Box>
                                <FormControlLabel
                                    className={classes.controlLabel}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={openMessages[invitation.id] || false}
                                            onChange={() => handleOpenMessage(invitation.id)}
                                        />
                                    }
                                    label="Personalized Message"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleOpenMessage(invitation.id);
                                    }}
                                />
                                {openMessages[invitation.id] && (
                                    <Box sx={{ padding: "0px 8px 8px 8px" }}>
                                        <TextField
                                            id={`message-${invitation.id}`}
                                            variant="standard"
                                            fullWidth
                                            multiline={true}
                                            rows={5}
                                            className={classes.messageTextField}
                                            placeholder={intl.formatMessage({
                                                id: "hubManagement.client.addUsers.inviteUser.messagePlaceholder",
                                            })}
                                            value={invitation.message}
                                            onChange={(e) => handleOnChange(invitation.id, "message", e.target.value)}
                                        />
                                    </Box>
                                )}
                            </Fragment>
                        ))}
                    </Box>
                    <LinkButton
                        className={classes.linkButton}
                        Icon={<AddCircleRoundedIcon className={classes.addIcon} />}
                        label={
                            <Typography sx={{ fontSize: 11 }}>
                                {intl.formatMessage({ id: "hubManagement.client.addUsers.inviteUser.addInvite" })}
                            </Typography>
                        }
                        onClick={handleAddInvitations}
                    />
                    <Box display="flex" justifyContent="end">
                        <Button
                            type="submit"
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircleOutlinedIcon />}
                            disabled={!areFieldsValid}
                            onClick={sendInvitations}
                            endIcon={invitationsData.length && <Typography>({invitationsData.length})</Typography>}
                        >
                            Send
                        </Button>
                    </Box>
                </CustomTabContent>

                {/* Tab Card Content for Pending Users*/}
                <CustomTabContent value={tabIndex} index={1}>
                    <Box className={classes.description}>
                        <Typography variant="subtitle2">
                            {intl.formatMessage({ id: "hubManagement.client.addUsers.pendingUsers.description" })}
                        </Typography>
                    </Box>
                    <Box sx={{ marginBottom: "8px" }}>
                        <TextField
                            size="small"
                            id="pending-users-fiter"
                            name="pending-user-filter"
                            autoComplete="off"
                            placeholder={intl.formatMessage({ id: "hubManagement.client.addUsers.filters.byUser" })}
                            fullWidth
                            onChange={handleFilterChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CustomIcon icon={icons.searchInverted} sx={{ fontSize: 14 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <TableContainer component={Paper} sx={{ maxHeight: "400px" }}>
                        <Table aria-label="pending-users-table" sx={{ border: "1px solid #DADEE5" }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell size="small" sx={{ display: "flex" }}>
                                        <CustomIcon icon={icons.sortArrows} className={classes.sortButton} />
                                        <Typography variant="subtitle2">User</Typography>
                                    </TableCell>
                                    <TableCell size="small" align="center">
                                        <Typography variant="subtitle2">Sent</Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredInv &&
                                    filteredInv.map((invitation) => {
                                        const timeAgo = invitation.sent_utc
                                            ? formatDistanceToNow(new Date(invitation.sent_utc), {
                                                  addSuffix: true,
                                              })
                                            : "No invite date";

                                        const isChecked = selectedInvitations.includes(invitation.id);

                                        return (
                                            <TableRow
                                                key={invitation.id}
                                                sx={{ background: "#F7F7F7" }}
                                                className={classes.tableRow}
                                            >
                                                <TableCell
                                                    size="small"
                                                    padding="checkbox"
                                                    className={classes.tableCell}
                                                >
                                                    <Box display="flex">
                                                        <Checkbox
                                                            size="small"
                                                            checked={isChecked}
                                                            onChange={(e) =>
                                                                handleCheckboxChange(invitation.id, e.target.checked)
                                                            }
                                                        />
                                                        <Box>
                                                            <Typography variant="body1">
                                                                {invitation.recipient_name}
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {invitation.recipient_email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    <Box display="flex" justifyContent="space-between">
                                                        <Box className={classes.tooltipWrapper}>
                                                            {timeAgo.includes("year") && (
                                                                <Tooltip
                                                                    arrow
                                                                    placement="bottom-start"
                                                                    title={intl.formatMessage({
                                                                        id: "hubManagement.client.addUsers.pendingUsers.tooltip",
                                                                    })}
                                                                >
                                                                    <span>
                                                                        <CustomIcon
                                                                            color="warning"
                                                                            icon={icons.errorTriangle}
                                                                            className={classes.warningIcon}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                        <Typography variant="body1" margin="auto">
                                                            {timeAgo}
                                                        </Typography>
                                                        <IconButton
                                                            id="submenu-actions-button"
                                                            onClick={(ev: React.MouseEvent<HTMLElement>) => {
                                                                setAnchorEl({
                                                                    element: ev.currentTarget,
                                                                    id: invitation.id,
                                                                });
                                                            }}
                                                        >
                                                            <MoreVertIcon />
                                                        </IconButton>
                                                        <Menu
                                                            sx={{
                                                                "& .MuiPaper-root": {
                                                                    boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.5)",
                                                                },
                                                            }}
                                                            open={openSubmenu && anchorEl.id === invitation.id}
                                                            anchorEl={anchorEl.element}
                                                            onClose={() => setAnchorEl({ element: null, id: null })}
                                                            anchorOrigin={{
                                                                vertical: "top",
                                                                horizontal: "left",
                                                            }}
                                                            transformOrigin={{
                                                                vertical: "top",
                                                                horizontal: "left",
                                                            }}
                                                        >
                                                            <MenuItem
                                                                disableRipple
                                                                onClick={async () => {
                                                                    await handleSendReminder([invitation.id]);
                                                                    setAnchorEl({ element: null, id: null });
                                                                    fetchUsersData();
                                                                }}
                                                            >
                                                                <Typography>Send Reminder</Typography>
                                                            </MenuItem>
                                                            {/* Don't remove this commented code. Should be used in a future requiremet */}
                                                            {/* <MenuItem disableRipple>
                                                            <Typography>Remove</Typography>
                                                        </MenuItem> */}
                                                        </Menu>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box display="flex" justifyContent="flex-end" sx={{ marginTop: "24px" }}>
                        {/* Don't remove this commented code. Should be used in a future requiremet */}
                        {/* <Button
                            size="small"
                            variant="contained"
                            disabled
                            startIcon={<DeleteOutlinedIcon />}
                            onClick={() => console.log("remove clicked!")}
                        >
                            Remove
                        </Button> */}
                        {/* NOTE: This empty Box was added in order to maintain the Send Reminder button in correct position
                         until we decide to add the Remove button */}
                        <Box></Box>
                        <Button
                            type="submit"
                            size="small"
                            variant="contained"
                            disabled={selectedInvitations.length === 0}
                            startIcon={<CheckCircleOutlinedIcon />}
                            onClick={async () => {
                                await handleSendReminder();
                                fetchUsersData();
                            }}
                        >
                            Send Reminder
                        </Button>
                    </Box>
                </CustomTabContent>
            </Box>
        </Modal>
    );
};

export default InviteUsersModal;
